import React, { useEffect, useState } from "react";
import { Typography, Toastify, toast, Tooltip } from "top-brains-ui";
import RegisterImage from "@src/assets/register-success-img.svg";
import styles from "@src/pages/login/registration-successful/RegistrationSuccessful.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useResendActivationLinkMutation } from "@src/api/LoginApiSlice";
import { capitalizeFirstLetter } from "@src/utils/CommonUtils";
import CommonLoader from "@src/components/loader/CommonLoader";
import { COMMON_COLORS } from "@src/styles/ColorConstants";
import { RootState } from "@src/app/store";
import GradiantButton from "@src/components/gradiant-btn/GradiantButton";
import { setUserLogin } from "@src/app/globalSlice";
import { useNavigate } from "react-router-dom";

const RegistrationSuccessful: React.FC = () => {
  const userId = useSelector(
    (state: RootState) => state.globalSlice?.studentDetails?.id,
  );
  const email = useSelector(
    (state: RootState) => state.globalSlice?.studentDetails?.email,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [resendActivationLink, { isLoading }] =
    useResendActivationLinkMutation();

  const [secondsLeft, setSecondsLeft] = useState(60);

  useEffect(() => {
    if (secondsLeft === 0) return;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  const handleResendActivation = async () => {
    if (!userId) {
      alert("User ID not found. Please log in again.");
      return;
    }
    try {
      const response = await resendActivationLink({ userId }).unwrap();
      toast.success("Success", capitalizeFirstLetter(response?.response));
      setSecondsLeft(60);
    } catch (error) {
      toast.error("Error", error?.data?.response);
    }
  };
  return (
    <>
      <div className={styles.container}>
        <div className={styles.cardContainer}>
          <div className={styles.card}>
            <div className={styles.msgTitle}>
              <Typography
                fontWeight="semi-bold"
                fontSize={26}
                color="var(--modal-heading-color)"
                className={styles.registrationHeading}
              >
                Registration Successful!
              </Typography>
            </div>
            <div className={styles.imageContainer}>
              <img src={RegisterImage} alt="register_img" />
            </div>

            <div className={styles.msgBody}>
              <div>
                <Typography
                  fontWeight="semi-bold"
                  fontSize={16}
                  color="var(--login-text-color)"
                >
                  Congratulations!{" "}
                </Typography>
                <br />
                <Typography
                  fontSize={16}
                  fontWeight="semi-bold"
                  color="var(--modal-heading-color)"
                >
                  Your registration has been successful. <br /> You are now a
                  member of our community.
                </Typography>
              </div>
              <div>
                <Typography
                  fontSize={16}
                  fontWeight="regular"
                  color="var(--modal-heading-color)"
                >
                  To complete your account activation, please check your{" "}
                  {email ? (
                    <span style={{ color: COMMON_COLORS.SUCCESS_GREEN }}>
                      ( {email} )
                    </span>
                  ) : null}{" "}
                  email and get activated.
                </Typography>
              </div>

              <div
                style={{
                  marginTop: "37px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {secondsLeft > 0 ? (
                  <Tooltip
                    title="Please wait before requesting another activation link."
                    placement="top"
                  >
                    <Typography
                      fontSize={14}
                      fontWeight="regular"
                      color="var(--modal-sub-heading-color, #FAFAFA)"
                      style={{
                        cursor: "not-allowed",
                        textDecoration: "underline",
                        opacity: "0.5",
                      }}
                    >
                      Resend Activation Link ({secondsLeft}s)
                    </Typography>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title="Didn’t get the email? Click to resend."
                    placement="top"
                  >
                    <Typography
                      fontSize={16}
                      fontWeight="semi-bold"
                      color="var(--modal-sub-heading-color, #A8A8A8)"
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={handleResendActivation}
                    >
                      Resend Activation Link
                    </Typography>
                  </Tooltip>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <GradiantButton
                  label="Back To SignIn"
                  width="250px"
                  variant="secondary"
                  img
                  className={styles.backToSignIn}
                  labelClassName={styles.backToSignInLabel}
                  onClick={() => {
                    navigate("/");
                    dispatch(setUserLogin(true));
                  }}
                />
              </div>
            </div>

            {isLoading && (
              <CommonLoader
                color={COMMON_COLORS.WEBSITE_GREEN_LIGHT}
                overLayColor={COMMON_COLORS.LOADER_OVERLAY}
              />
            )}
          </div>
        </div>
      </div>
      <Toastify />
    </>
  );
};

export default RegistrationSuccessful;
